import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConversation } from '../hooks/useConversation.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { getConversation } from '../services/api.js';
import { logger } from '../utils/logger.js';

/**
 * Applies ?session_id= once (local match or API load/fork), then strips it.
 * An in-flight inbound fetch is not cancelled when local sessions update.
 */
const ConversationQuerySync = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionsObject, sessionsLoaded, currentSessionId, switchSession, hydrateSession } = useConversation();
  const { companies, switchCompany, activeCompany } = useAuth();
  const appliedParamRef = useRef(null);
  const inboundRef = useRef(null);

  const stripSessionId = useCallback((companyId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('session_id');
      if (companyId != null) next.set('company', String(companyId));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!sessionsLoaded) return;
    const param = searchParams.get('session_id');
    if (!param) {
      appliedParamRef.current = null;
      inboundRef.current = null;
      return;
    }
    if (appliedParamRef.current === param || inboundRef.current === param) {
      return;
    }

    const match = Object.values(sessionsObject).find(
      (session) => session.id === param
    );
    if (match) {
      const company = companies.find((c) => String(c.id) === String(match.company_id));
      if (match.id !== currentSessionId) {
        switchSession(match.id);
      }
      if (company && String(activeCompany?.id) !== String(company.id)) {
        switchCompany(company);
      }
      appliedParamRef.current = param;
      stripSessionId(company ? company.id : null);
      return;
    }

    inboundRef.current = param;
    (async () => {
      try {
        const data = await getConversation(param);
        if (appliedParamRef.current === param) return;
        const company = companies.find((c) => String(c.id) === String(data.company_id));
        if (!company) {
          stripSessionId(null);
          appliedParamRef.current = param;
          return;
        }
        hydrateSession({
          sessionId: data.session_id,
          companyId: data.company_id,
          title: data.title,
          messages: data.messages || []
        });
        if (String(activeCompany?.id) !== String(company.id)) {
          switchCompany(company);
        }
        appliedParamRef.current = param;
        stripSessionId(company.id);
      } catch (error) {
        if (error.status === 404) {
          stripSessionId(null);
          appliedParamRef.current = param;
          return;
        }
        inboundRef.current = null;
        logger.error('Failed to load conversation:', error);
      }
    })();
  }, [
    sessionsLoaded,
    searchParams,
    sessionsObject,
    currentSessionId,
    switchSession,
    hydrateSession,
    companies,
    switchCompany,
    activeCompany?.id,
    stripSessionId
  ]);

  return null;
};

export default ConversationQuerySync;
